import { useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { useFeature } from 'src/app/FeatureToggle';
import type {
  PayerOrganizationRead,
  PayerOrganizationTripRequiredFieldsRead,
} from 'src/common/external/bambi-api/bambiApi';

import type { TripSubscriptionFormValues } from '../trip/trip-subscriptions/trip-subscription-form/FormValues';
import type { AddTripFormValues } from './FormValues';
import { pricingNameRoot } from './Pricing/pricingNameRoot';

export const usePayerTripRequiredField = (
  field: keyof PayerOrganizationTripRequiredFieldsRead,
  selectedPayer: PayerOrganizationRead | null
) => {
  const { setValue, clearErrors, getValues } = useFormContext();
  const { isEnabled: isFeatureFlagEnabled, isLoading: isFeatureFlagLoading } =
    useFeature('trip required fields');

  const isFieldRequired = useMemo(
    () => !!selectedPayer?.trip_required_fields?.[field],
    [field, selectedPayer?.trip_required_fields]
  );

  useEffect(() => {
    const clearFieldAndErrors = (fieldName: string) => {
      setValue(fieldName, '');
      clearErrors(fieldName);
    };

    const clearExternalTripIds = () => {
      const values = getValues() as
        | AddTripFormValues
        | TripSubscriptionFormValues;

      // If the form is an add a trip form, clear the external trip ids
      if ('trips' in values) {
        const trips = values.trips;

        const fieldNames = Array.from(
          { length: trips.length },
          (_, i) => `trips.${i}.external_trip_id`
        );

        fieldNames.forEach((fieldName) => clearFieldAndErrors(fieldName));
      }
    };

    // Clear broker-specific field if not a broker
    if (selectedPayer && selectedPayer.payer_type !== 'broker') {
      clearFieldAndErrors(`${pricingNameRoot}.payer_passenger_external_id`);
      clearExternalTripIds();
    }

    // Clear insurance-specific fields if not insurance
    if (selectedPayer && selectedPayer.payer_type !== 'insurance') {
      clearFieldAndErrors(`${pricingNameRoot}.authorization_id`);
      clearFieldAndErrors(`${pricingNameRoot}.insurance_policy_id`);
    }
  }, [clearErrors, setValue, selectedPayer, getValues]);

  if (isFeatureFlagLoading || !isFeatureFlagEnabled) {
    return false;
  }

  return isFieldRequired;
};
